import React from "react";
import PropTypes from "prop-types";
import { toTitleCaseLabel } from "./leadSummaryLabels";

function isPlainObject(value) {
    return value != null && typeof value === "object" && !Array.isArray(value);
}

function normalizeDisplayText(value) {
    const s = value == null ? "" : String(value).trim();
    if (!s) {
        return "";
    }
    return s.replace(/^:\s*/, "");
}

function hasDisplayText(value) {
    return normalizeDisplayText(value).length > 0;
}

function isPrimitiveValue(value) {
    return (
        typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    );
}

export function isEnrollmentLikelihoodKey(key) {
    const normalized = String(key || "")
        .toLowerCase()
        .replace(/[_\s-]+/g, "");
    return normalized.includes("ecmenrollmentlikelihood");
}

/** Enrollment % string from `lead_summary` (any key spelling). */
export function getEnrollmentLikelihood(leadSummary) {
    if (!isPlainObject(leadSummary)) {
        return "";
    }
    const key = Object.keys(leadSummary).find(isEnrollmentLikelihoodKey);
    return key ? leadSummary[key] : "";
}

/** Placeholder for empty / unavailable demographic fields on lead summary UI. */
export const DEMOGRAPHIC_PLACEHOLDER = "-";

/** Blank, null, "Unavailable", or "Not Available" (case-insensitive). */
export function isUnavailableDisplayValue(value) {
    if (value == null) {
        return true;
    }
    const s = String(value).trim();
    if (!s) {
        return true;
    }
    return /^(not\s+available|unavailable)$/i.test(s);
}

/** Male → M, Female → F, Other → U; Unknown / Unavailable → "-". */
export function formatGenderDisplay(value) {
    if (value == null) {
        return DEMOGRAPHIC_PLACEHOLDER;
    }
    const gender = String(value).trim().toLowerCase();
    if (
        !gender ||
        /^(not\s+available|unavailable|unknown|unk|n\/a|-|—|–)$/i.test(gender)
    ) {
        return DEMOGRAPHIC_PLACEHOLDER;
    }
    if (gender === "male" || gender === "m") {
        return "M";
    }
    if (gender === "female" || gender === "f") {
        return "F";
    }
    if (gender === "other") {
        return "U";
    }
    return String(value).trim();
}

function isUnavailableContactValue(value) {
    return isUnavailableDisplayValue(value);
}

/**
 * Format one phone token as `(NNN) NNN-NNNN` (10 digits).
 * 11-digit values drop a leading `1` (US country code) before formatting.
 * @param {string} token
 * @returns {string}
 */
function formatOnePhoneToken(token) {
    const raw = String(token ?? "").trim();
    if (isUnavailableDisplayValue(raw)) {
        return "";
    }
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
        return raw;
    }
    let normalized = digits;
    if (normalized.length === 11 && normalized.startsWith("1")) {
        normalized = normalized.slice(1);
    }
    if (normalized.length === 10) {
        return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
    }
    return raw;
}

/**
 * Phone from leads list row: `(209) 495-5804` for 10 digits; strips leading `1` on 11-digit numbers.
 * Supports arrays and multiple numbers in one string (newline, comma, semicolon, `and`).
 */
export function formatLeadPhoneNumber(value) {
    if (value == null) {
        return "";
    }
    if (Array.isArray(value)) {
        return value
            .map((x) => formatOnePhoneToken(x))
            .filter(Boolean)
            .join("\n");
    }
    const s = String(value).trim();
    if (isUnavailableDisplayValue(s)) {
        return "";
    }
    const parts = s
        .split(/\s+and\s+|[\n;,|]+/i)
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length > 1) {
        return parts
            .map((p) => formatOnePhoneToken(p))
            .filter(Boolean)
            .join("\n");
    }
    return formatOnePhoneToken(s);
}

/** Phone / email from leads list row. */
export function formatLeadListContactValue(value) {
    if (value == null) {
        return "";
    }
    if (Array.isArray(value)) {
        const lines = value
            .map((x) => String(x ?? "").trim())
            .filter((x) => x && !isUnavailableDisplayValue(x));
        return lines.join("\n");
    }
    if (isUnavailableContactValue(value)) {
        return "";
    }
    return String(value).trim();
}

/**
 * One address: street on line 1 (with trailing comma), city/state/zip on line 2.
 * Example: "2229 Manitoba Ct, Modesto, Ca 95356" -> "2229 Manitoba Ct,\nModesto, Ca 95356"
 */
function formatSingleLeadAddress(addr) {
    const s = String(addr ?? "").trim();
    if (isUnavailableDisplayValue(s)) {
        return "";
    }
    const firstComma = s.indexOf(",");
    if (firstComma < 0) {
        return s;
    }
    const street = s.slice(0, firstComma).trim();
    const locality = s.slice(firstComma + 1).trim();
    if (!locality) {
        return street;
    }
    return `${street},\n${locality}`;
}

/** Formatted address lines (one entry per address, each may contain `\n`). */
export function getLeadFormattedAddressEntries(value) {
    if (value == null) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map((x) => formatSingleLeadAddress(x)).filter(Boolean);
    }
    if (isUnavailableContactValue(value)) {
        return [];
    }
    const single = formatSingleLeadAddress(value);
    return single ? [single] : [];
}

/** Address from leads list row (string or string[]). */
export function formatLeadListAddressValue(value) {
    return getLeadFormattedAddressEntries(value).join("\n");
}

const MONTHS_SHORT = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];

function parseDobComponents(dob) {
    const raw = String(dob ?? "").trim();
    if (!raw) {
        return null;
    }
    const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) {
        return {
            year: Number(slash[3]),
            month: Number(slash[1]),
            day: Number(slash[2])
        };
    }
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        return {
            year: Number(iso[1]),
            month: Number(iso[2]),
            day: Number(iso[3])
        };
    }
    return null;
}

function parseIsoDateTimeWallClock(raw) {
    const s = String(raw || "").trim();
    if (!s) {
        return null;
    }
    const match = s.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/i
    );
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }
    const hour = match[4] != null ? Number(match[4]) : null;
    const minute = match[5] != null ? Number(match[5]) : null;
    if (hour != null && (!Number.isFinite(hour) || hour < 0 || hour > 23)) {
        return null;
    }
    if (minute != null && (!Number.isFinite(minute) || minute < 0 || minute > 59)) {
        return null;
    }
    return { year, month, day, hour, minute };
}

function formatWallClockTime12(hour, minute) {
    const h24 = hour ?? 0;
    const min = minute ?? 0;
    const period = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h12 === 0) {
        h12 = 12;
    }
    return `${h12}:${String(min).padStart(2, "0")} ${period}`;
}

/** `Jan 24, 1999` — uses API date parts as-is (no timezone conversion). */
export function formatDisplayDob(dob) {
    const parts = parseDobComponents(dob);
    if (parts) {
        const month = MONTHS_SHORT[parts.month - 1];
        if (month) {
            return `${month} ${parts.day}, ${parts.year}`;
        }
    }
    return String(dob ?? "").trim();
}

/** Split DOB into fixed display parts for aligned list columns. */
export function formatDisplayDobParts(dob) {
    const parts = parseDobComponents(dob);
    if (!parts) {
        return null;
    }
    const month = MONTHS_SHORT[parts.month - 1];
    if (!month) {
        return null;
    }
    return {
        month,
        day: String(parts.day).padStart(2, "0"),
        year: String(parts.year)
    };
}

/** `21 Years` or `21 yrs` when style is `short`. Single-digit ages pad to 2 digits (`05 yrs`). */
export function formatAgeYears(age, style = "long") {
    const ageText = String(age ?? "").trim();
    if (!ageText) {
        return "";
    }
    const unit = style === "short" ? "yrs" : "Years";
    const numeric = ageText.match(/^(\d{1,3})/);
    if (numeric) {
        const padded =
            style === "short" ? String(numeric[1]).padStart(2, "0") : numeric[1];
        return `${padded} ${unit}`;
    }
    if (/years?$/i.test(ageText) || /yrs$/i.test(ageText)) {
        if (style === "short") {
            const match = ageText.match(/^(\d{1,3})\s*(years?|yrs)$/i);
            if (match) {
                return `${String(match[1]).padStart(2, "0")} ${unit}`;
            }
        }
        return ageText.replace(/(years?|yrs)$/i, unit);
    }
    return `${ageText} ${unit}`;
}

/** `Jan 24, 1999 (21 Years)` — detail demographics value. */
export function formatDobWithAge(dob, age, ageStyle = "long") {
    const dobText = formatDisplayDob(dob);
    const ageDisplay = formatAgeYears(age, ageStyle);
    if (!dobText && !ageDisplay) {
        return "";
    }
    if (!dobText) {
        return ageDisplay ? `(${ageDisplay})` : "";
    }
    if (!ageDisplay) {
        return dobText;
    }
    return `${dobText} (${ageDisplay})`;
}

/** `DOB: Jan 24, 1999 (21 Years)` — leads list row (plain text). */
export function formatDobAgeLine(dob, age) {
    const value = formatDobWithAge(dob, age);
    if (!value) {
        return "";
    }
    return `DOB: ${value}`;
}

/** DOB + age for list/detail; date and age render in separate containers. */
export function DobWithAgeDisplay({ dob, age, prefix = "", ageStyle = "long" }) {
    const ageDisplay = formatAgeYears(age, ageStyle);
    const parts = ageStyle === "short" ? formatDisplayDobParts(dob) : null;
    const dobText = parts ? "" : formatDisplayDob(dob);

    if (!parts && !dobText && !ageDisplay) {
        return null;
    }

    return (
        <span
            className={
                ageStyle === "short"
                    ? "leads-dob-with-age leads-dob-with-age--fixed"
                    : "leads-dob-with-age"
            }
        >
            {prefix ? <span className="leads-dob-with-age__prefix">{prefix}</span> : null}
            {parts ? (
                <span className="leads-dob-with-age__date">
                    <span className="leads-dob-with-age__month">{parts.month}</span>
                    <span className="leads-dob-with-age__gap" aria-hidden="true">
                        {"\u00A0"}
                    </span>
                    <span className="leads-dob-with-age__day">{parts.day}</span>
                    <span className="leads-dob-with-age__comma">,</span>
                    <span className="leads-dob-with-age__gap" aria-hidden="true">
                        {"\u00A0"}
                    </span>
                    <span className="leads-dob-with-age__year">{parts.year}</span>
                </span>
            ) : dobText ? (
                <span className="leads-dob-with-age__date">{dobText}</span>
            ) : null}
            {ageDisplay ? (
                <span className="leads-dob-with-age__age">
                    {"\u00A0"}({ageDisplay})
                </span>
            ) : null}
        </span>
    );
}

DobWithAgeDisplay.propTypes = {
    dob: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prefix: PropTypes.string,
    ageStyle: PropTypes.oneOf(["long", "short"])
};

export function buildDemographicsFromLeadRow(row, cinFallback = "") {
    const first = String(row?.first_name || "").trim();
    const last = String(row?.last_name || "").trim();
    const name = [first, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    const cin = String(row?.cin || cinFallback || "").trim();
    return {
        name,
        row1: [
            ["CIN", cin],
            ["DOB", { dob: row?.dob, age: row?.age }],
            ["Gender", row?.gender],
            ["Language", row?.preferred_language]
        ],
        row2: [
            ["Phone", formatLeadPhoneNumber(row?.phone)],
            ["Email", formatLeadListContactValue(row?.email)],
            ["Address", row?.address]
        ]
    };
}

function valueHasContent(value) {
    if (value == null) {
        return false;
    }
    if (isPrimitiveValue(value)) {
        return hasDisplayText(value);
    }
    if (Array.isArray(value)) {
        return value.some((item) => valueHasContent(item));
    }
    if (isPlainObject(value)) {
        return Object.keys(value).some((k) => valueHasContent(value[k]));
    }
    return false;
}

function headingClassForDepth(depth) {
    if (depth <= 0) {
        return "lead-detail-section__title";
    }
    if (depth === 1) {
        return "lead-summary-h4";
    }
    if (depth === 2) {
        return "lead-summary-h5";
    }
    return "lead-summary-h6";
}

function LeadSummaryHeading({ depth, children }) {
    const className = headingClassForDepth(depth);
    if (depth <= 0) {
        return <h3 className={className}>{children}</h3>;
    }
    if (depth === 1) {
        return <h4 className={className}>{children}</h4>;
    }
    if (depth === 2) {
        return <h5 className={className}>{children}</h5>;
    }
    return <h6 className={className}>{children}</h6>;
}

function LeadSummaryBulletList({ lines, listKey }) {
    const items = (Array.isArray(lines) ? lines : [lines])
        .map((line) => normalizeDisplayText(line))
        .filter(Boolean);
    if (!items.length) {
        return null;
    }
    return (
        <ul className="lead-summary-list" key={listKey}>
            {items.map((line, i) => (
                <li key={listKey != null ? `${listKey}-${i}` : i}>{line}</li>
            ))}
        </ul>
    );
}

/** Object of string fields only (e.g. Signal / May Indicate, or Utilization History rows). */
function isLabeledPrimitiveMap(value) {
    if (!isPlainObject(value)) {
        return false;
    }
    const entries = Object.entries(value);
    return entries.length > 0 && entries.every(([, v]) => isPrimitiveValue(v) && hasDisplayText(v));
}

/** Risk item: { Signal, May Indicate, Matters If Unaddressed, ... } */
function isRiskSignalDetailObject(value) {
    if (!isLabeledPrimitiveMap(value)) {
        return false;
    }
    const keys = Object.keys(value).map((k) => k.toLowerCase());
    return keys.some((k) => k.includes("signal") || k.includes("indicate") || k.includes("matters"));
}

function LeadSummaryLabeledBulletList({ fields, listKey }) {
    const items = Object.entries(fields || {}).filter(([, v]) => hasDisplayText(v));
    if (!items.length) {
        return null;
    }
    return (
        <ul className="lead-summary-list lead-summary-list--labeled" key={listKey}>
            {items.map(([fieldLabel, fieldValue]) => (
                <li key={`${listKey}-${fieldLabel}`}>
                    <strong>{toTitleCaseLabel(fieldLabel)}:</strong> {normalizeDisplayText(fieldValue)}
                </li>
            ))}
        </ul>
    );
}

function LeadSummaryBlock({ label, value, depth }) {
    const displayLabel = toTitleCaseLabel(label);
    if (isPrimitiveValue(value)) {
        const text = normalizeDisplayText(value);
        if (!text) {
            return null;
        }
        return (
            <div className={`lead-summary-block lead-summary-block--depth-${Math.min(depth, 3)}`}>
                <LeadSummaryHeading depth={depth}>{displayLabel}</LeadSummaryHeading>
                <LeadSummaryBulletList lines={[text]} listKey={`block-${depth}-${label}`} />
            </div>
        );
    }

    if (isRiskSignalDetailObject(value)) {
        return (
            <div
                className={`lead-summary-block lead-summary-block--depth-${Math.min(depth, 3)} lead-summary-block--risk`}
            >
                <LeadSummaryHeading depth={depth}>{displayLabel}</LeadSummaryHeading>
                <LeadSummaryLabeledBulletList fields={value} listKey={`risk-${depth}-${label}`} />
            </div>
        );
    }

    if (isLabeledPrimitiveMap(value)) {
        return (
            <div className={`lead-summary-block lead-summary-block--depth-${Math.min(depth, 3)}`}>
                <LeadSummaryHeading depth={depth}>{displayLabel}</LeadSummaryHeading>
                <LeadSummaryLabeledBulletList
                    fields={value}
                    listKey={`labeled-${depth}-${label}`}
                />
            </div>
        );
    }

    return (
        <div className={`lead-summary-block lead-summary-block--depth-${Math.min(depth, 3)}`}>
            <LeadSummaryHeading depth={depth}>{displayLabel}</LeadSummaryHeading>
            <LeadSummaryValue value={value} depth={depth + 1} />
        </div>
    );
}

function LeadSummaryValue({ value, depth = 0 }) {
    if (value == null) {
        return null;
    }

    if (isPrimitiveValue(value)) {
        const text = normalizeDisplayText(value);
        if (!text) {
            return null;
        }
        // Top-level section narrative (e.g. Situation) — prose paragraph, not bullets.
        if (depth === 0) {
            return (
                <p className="lead-doc-paragraph lead-doc-paragraph--situation">{text}</p>
            );
        }
        return <LeadSummaryBulletList lines={[text]} listKey={`prim-${depth}`} />;
    }

    if (Array.isArray(value)) {
        const items = value.filter((x) => valueHasContent(x));
        if (!items.length) {
            return null;
        }

        const primitiveLines = items
            .filter((x) => isPrimitiveValue(x))
            .map((x) => normalizeDisplayText(x))
            .filter(Boolean);
        const complexItems = items.filter((x) => !isPrimitiveValue(x));

        return (
            <div className="lead-summary-group">
                {primitiveLines.length > 0 ? (
                    <LeadSummaryBulletList lines={primitiveLines} listKey={`arr-prim-${depth}`} />
                ) : null}
                {complexItems.map((item, i) => {
                    if (isPlainObject(item)) {
                        return (
                            <div className="lead-summary-group" key={`arr-obj-${i}`}>
                                {Object.entries(item)
                                    .filter(([, v]) => valueHasContent(v))
                                    .map(([label, val]) => (
                                        <LeadSummaryBlock
                                            key={`${i}-${label}`}
                                            label={label}
                                            value={val}
                                            depth={depth}
                                        />
                                    ))}
                            </div>
                        );
                    }
                    return (
                        <LeadSummaryValue key={`arr-nested-${i}`} value={item} depth={depth} />
                    );
                })}
            </div>
        );
    }

    if (isPlainObject(value)) {
        const entries = Object.entries(value).filter(([, v]) => valueHasContent(v));
        if (!entries.length) {
            return null;
        }
        return (
            <div className="lead-summary-outline">
                {entries.map(([label, val]) => (
                    <LeadSummaryBlock key={label} label={label} value={val} depth={depth} />
                ))}
            </div>
        );
    }

    return null;
}

/**
 * Renders `lead_summary` in JSON key order (sections → subsections → lines).
 * Skips enrollment likelihood (rendered separately as the enrollment badge).
 */
export function LeadSummaryDynamicSections({ leadSummary }) {
    if (!isPlainObject(leadSummary)) {
        return null;
    }

    const sections = Object.entries(leadSummary).filter(
        ([key, value]) => !isEnrollmentLikelihoodKey(key) && valueHasContent(value)
    );

    if (!sections.length) {
        return null;
    }

    return (
        <>
            {sections.map(([sectionTitle, sectionValue]) => (
                <section
                    className="lead-detail-section"
                    key={sectionTitle}
                    aria-label={sectionTitle}
                >
                    <h2 className="lead-summary-h2">{toTitleCaseLabel(sectionTitle)}</h2>
                    <LeadSummaryValue value={sectionValue} depth={0} />
                </section>
            ))}
        </>
    );
}

export function LeadSummarySectionBody({ value, depth = 0 }) {
    return <LeadSummaryValue value={value} depth={depth} />;
}

LeadSummarySectionBody.propTypes = {
    value: PropTypes.any,
    depth: PropTypes.number
};

function normalizeKeyToken(key) {
    return String(key || "")
        .toLowerCase()
        .replace(/[_\s-/]+/g, "");
}

export function isKeyQuestionsKey(key) {
    const n = normalizeKeyToken(key);
    return n.includes("keyquestions");
}

export function isRecommendationKey(key) {
    const n = normalizeKeyToken(key);
    return n === "recommendation" || n.startsWith("recommendation");
}

export function isSituationKey(key) {
    return normalizeKeyToken(key) === "situation";
}

export function isBackgroundKey(key) {
    return normalizeKeyToken(key) === "background";
}

export function isAssessmentKey(key) {
    return normalizeKeyToken(key) === "assessment";
}

export function isOverallPatientSummaryKey(key) {
    const n = normalizeKeyToken(key);
    return n === "overallpatientsummary" || n === "overallsummary";
}

export function isHighPrioritySignalsKey(key) {
    const n = normalizeKeyToken(key);
    return n.includes("highpriority") || n.includes("earlyrisk");
}

export function isOutreachBriefKey(key) {
    // API renamed Outreach Brief → Synopsis; keep legacy key for older payloads.
    const n = normalizeKeyToken(key);
    return n === "synopsis" || n === "outreachbrief";
}

/**
 * Split `lead_summary` into main panel sections + sidebar payloads.
 * @returns {{
 *   overallCallout: string,
 *   overallExtra: Record<string, unknown>|null,
 *   situation: unknown,
 *   background: unknown,
 *   assessment: unknown,
 *   otherSections: Array<[string, unknown]>,
 *   recommendations: Array<{ title: string, body: string }>,
 *   keyQuestionGroups: Array<{ group: string, questions: string[] }>
 * }}
 */
export function partitionLeadSummary(leadSummary) {
    const empty = {
        overallCallout: "",
        overallExtra: null,
        situation: null,
        background: null,
        assessment: null,
        otherSections: [],
        recommendations: [],
        keyQuestionGroups: []
    };
    if (!isPlainObject(leadSummary)) {
        return empty;
    }

    let situation = null;
    let background = null;
    let assessmentRaw = null;
    let recommendationRaw = null;
    let keyQuestionsRaw = null;
    let synopsisRaw = null;
    const otherSections = [];

    for (const [key, value] of Object.entries(leadSummary)) {
        if (isEnrollmentLikelihoodKey(key) || !valueHasContent(value)) {
            continue;
        }
        if (isSituationKey(key)) {
            situation = value;
        } else if (isBackgroundKey(key)) {
            background = value;
        } else if (isAssessmentKey(key)) {
            assessmentRaw = value;
        } else if (isRecommendationKey(key)) {
            recommendationRaw = value;
        } else if (isKeyQuestionsKey(key)) {
            keyQuestionsRaw = value;
        } else if (isOutreachBriefKey(key)) {
            synopsisRaw = value;
        } else {
            otherSections.push([key, value]);
        }
    }

    // Newer payloads nest "Key Questions" under Recommendation instead of top-level.
    if (!keyQuestionsRaw && isPlainObject(recommendationRaw)) {
        const nestedKq = Object.entries(recommendationRaw).find(([k]) => isKeyQuestionsKey(k));
        if (nestedKq) {
            keyQuestionsRaw = nestedKq[1];
            recommendationRaw = Object.fromEntries(
                Object.entries(recommendationRaw).filter(([k]) => !isKeyQuestionsKey(k))
            );
        }
    }

    const overallCallout = "";
    const overallExtra = valueHasContent(synopsisRaw) ? synopsisRaw : null;
    const assessment = assessmentRaw;

    return {
        overallCallout,
        overallExtra,
        situation,
        background,
        assessment,
        otherSections,
        recommendations: flattenRecommendationItems(recommendationRaw),
        keyQuestionGroups: normalizeKeyQuestionGroups(keyQuestionsRaw)
    };
}

/** True when synopsis / Q&A / recommendations have something to render. */
export function leadSummaryHasRenderableBody(leadSummary) {
    const partitioned = partitionLeadSummary(leadSummary);
    return Boolean(
        partitioned.overallCallout ||
            valueHasContent(partitioned.overallExtra) ||
            valueHasContent(partitioned.situation) ||
            valueHasContent(partitioned.background) ||
            valueHasContent(partitioned.assessment) ||
            partitioned.otherSections.some(([, value]) => valueHasContent(value)) ||
            partitioned.recommendations.length > 0 ||
            partitioned.keyQuestionGroups.some((group) => group.questions.length > 0)
    );
}

function flattenRecommendationItems(value) {
    const items = [];
    if (!valueHasContent(value)) {
        return items;
    }
    if (isPrimitiveValue(value)) {
        const text = normalizeDisplayText(value);
        if (text) {
            items.push({ title: "", body: text });
        }
        return items;
    }
    if (Array.isArray(value)) {
        for (const entry of value) {
            if (isPrimitiveValue(entry)) {
                const text = normalizeDisplayText(entry);
                if (text) {
                    items.push({ title: "", body: text });
                }
            } else if (isPlainObject(entry)) {
                items.push(...flattenRecommendationItems(entry));
            }
        }
        return items;
    }
    if (isPlainObject(value)) {
        // Prefer nested "Recommended Next Steps" maps when present.
        const nestedStepsKey = Object.keys(value).find((k) =>
            normalizeKeyToken(k).includes("recommendednext")
        );
        if (nestedStepsKey && isPlainObject(value[nestedStepsKey])) {
            return flattenRecommendationItems(value[nestedStepsKey]);
        }
        for (const [label, val] of Object.entries(value)) {
            if (isKeyQuestionsKey(label) || !valueHasContent(val)) {
                continue;
            }
            if (isPrimitiveValue(val)) {
                items.push({
                    title: toTitleCaseLabel(label),
                    body: normalizeDisplayText(val)
                });
            } else if (isLabeledPrimitiveMap(val)) {
                for (const [subLabel, subVal] of Object.entries(val)) {
                    if (!hasDisplayText(subVal)) {
                        continue;
                    }
                    items.push({
                        title: toTitleCaseLabel(subLabel),
                        body: normalizeDisplayText(subVal)
                    });
                }
            } else {
                items.push(...flattenRecommendationItems(val));
            }
        }
    }
    return items;
}

function normalizeKeyQuestionGroups(value) {
    if (!valueHasContent(value)) {
        return [];
    }
    if (Array.isArray(value)) {
        const questions = value
            .filter((x) => isPrimitiveValue(x))
            .map((x) => normalizeDisplayText(x))
            .filter(Boolean);
        return questions.length ? [{ group: "Key Questions", questions }] : [];
    }
    if (!isPlainObject(value)) {
        return [];
    }
    return Object.entries(value)
        .map(([group, questions]) => {
            const list = Array.isArray(questions)
                ? questions
                      .filter((x) => isPrimitiveValue(x))
                      .map((x) => normalizeDisplayText(x))
                      .filter(Boolean)
                : isPrimitiveValue(questions) && hasDisplayText(questions)
                  ? [normalizeDisplayText(questions)]
                  : [];
            return {
                group: formatKeyQuestionGroupLabel(group),
                questions: list
            };
        })
        .filter((g) => g.questions.length > 0);
}

/** Drop API-only `POF:` prefix from key-question group keys for UI display. */
export function formatKeyQuestionGroupLabel(groupName) {
    const cleaned = String(groupName || "")
        .trim()
        .replace(/^POF\s*:\s*/i, "")
        .trim();
    return cleaned || "Questions";
}

/** API fallback group label when no Population of Focus was detected. */
export function isNoPofQuestionGroupName(groupName) {
    return /^no\s+pof\s+identified\b/i.test(String(groupName || "").trim());
}

/** `Jun 17, 2026 • 4:01 PM` — uses API date/time parts as-is (no timezone conversion). */
export function formatLeadSummaryTimestamp(iso) {
    const raw = String(iso || "").trim();
    if (!raw) {
        return "";
    }
    const parts = parseIsoDateTimeWallClock(raw);
    if (parts) {
        const month = MONTHS_SHORT[parts.month - 1];
        if (month) {
            const datePart = `${month} ${parts.day}, ${parts.year}`;
            if (parts.hour != null && parts.minute != null) {
                return `${datePart} • ${formatWallClockTime12(parts.hour, parts.minute)}`;
            }
            return datePart;
        }
    }
    return raw;
}
